/**
 * /platform/permissions - read-only catalog of every permission code
 * registered in ``permission_tbl``, sourced from
 * ``GET /admin/v1/permissions``. Gated to super_admin on the backend
 * (``permission.view``); non-super admins land a 403.
 *
 * Display: codes grouped by category, each row showing the dotted
 * code, human description, supports_scope flag, and the list of valid
 * scopes. No mutations - grants live on the role, not on the
 * permission itself.
 */

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";

import type { PermissionDef } from "@tilt/admin-sdk";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { usePageTitle } from "@/lib/use-page-title";

export function PermissionsCatalogPage(): JSX.Element {
  usePageTitle("Permission catalog");

  const query = useQuery({
    queryKey: ["permissions", "list"],
    queryFn: () => apiClient.listPermissions(),
    staleTime: 5 * 60_000,
  });

  const grouped = useMemo(() => {
    if (!query.data) return [];
    return groupByCategory(query.data.permissions);
  }, [query.data]);

  if (query.isLoading) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading permission catalog…
      </p>
    );
  }

  if (query.isError) {
    return (
      <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
        {describe(query.error)}
      </div>
    );
  }

  const total = query.data?.permissions.length ?? 0;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Permission catalog</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {total} code{total === 1 ? "" : "s"} registered in the platform. Grants live on roles -
          edit them from the{" "}
          <a href="/platform/roles" className="underline hover:text-foreground">
            Roles page
          </a>
          .
        </p>
      </div>

      {grouped.length === 0 ? (
        <p className="rounded-md border border-dashed bg-muted/30 p-4 text-xs text-muted-foreground">
          No permissions found.
        </p>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <section key={g.category} className="overflow-hidden rounded-lg border bg-card">
              <header className="border-b bg-muted/30 px-4 py-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                  {g.category}
                </h2>
              </header>
              <table className="w-full text-xs">
                <thead className="border-b bg-muted/10">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Code</th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                      Description
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                      Valid scopes
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {g.permissions.map((p) => (
                    <tr key={p.code} className="hover:bg-muted/20">
                      <td className="px-4 py-2 font-mono text-xs text-strong">{p.code}</td>
                      <td className="px-4 py-2 text-muted-foreground">{p.description}</td>
                      <td className="px-4 py-2">
                        {p.supports_scope ? (
                          <span className="flex flex-wrap gap-1">
                            {p.valid_scopes.map((s) => (
                              <span
                                key={s}
                                className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700"
                              >
                                {s}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

interface CategoryGroup {
  readonly category: string;
  readonly permissions: readonly PermissionDef[];
}

function groupByCategory(perms: readonly PermissionDef[]): readonly CategoryGroup[] {
  const map = new Map<string, PermissionDef[]>();
  for (const p of perms) {
    const list = map.get(p.category);
    if (list) {
      list.push(p);
    } else {
      map.set(p.category, [p]);
    }
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, list]) => ({
      category,
      permissions: list.slice().sort((a, b) => a.code.localeCompare(b.code)),
    }));
}
